/** Configurable client-side limits for OS video uploads (canvas and chat). */

import pluralize from 'pluralize';

// biome-ignore lint/style/noMagicNumbers: 1024 * 1024 is the idiomatic way to express bytes-per-megabyte
const BYTES_PER_MB = 1024 * 1024;
const CANVAS_VIDEO_UPLOAD_MAX_FILE_SIZE_MB = 100;

export const CANVAS_VIDEO_UPLOAD_MAX_FILE_SIZE_BYTES = CANVAS_VIDEO_UPLOAD_MAX_FILE_SIZE_MB * BYTES_PER_MB;
export const CANVAS_VIDEO_UPLOAD_MAX_DURATION_SECONDS = 30;

export type CanvasVideoUploadRejectionReason = 'size' | 'duration' | 'both' | 'unknown';

export const getCanvasVideoUploadMaxSizeLabel = (): string =>
  `${CANVAS_VIDEO_UPLOAD_MAX_FILE_SIZE_BYTES / BYTES_PER_MB}MB`;

export const isVideoOverSizeLimit = (sizeBytes: number): boolean => sizeBytes > CANVAS_VIDEO_UPLOAD_MAX_FILE_SIZE_BYTES;

/** True when duration is missing, invalid, or over the max; exactly the max is allowed. */
export const isVideoDurationInvalidOrOverLimit = (durationSeconds: number | null | undefined): boolean => {
  if (durationSeconds == null || !Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return true;
  }

  return durationSeconds > CANVAS_VIDEO_UPLOAD_MAX_DURATION_SECONDS;
};

export const getVideoUploadRejectionReason = ({
  durationSeconds,
  sizeBytes,
}: {
  durationSeconds: number | null | undefined;
  sizeBytes: number;
}): CanvasVideoUploadRejectionReason | null => {
  const overSize = isVideoOverSizeLimit(sizeBytes);
  const durationUnknown = durationSeconds == null || !Number.isFinite(durationSeconds) || durationSeconds <= 0;
  const overDuration = !durationUnknown && durationSeconds > CANVAS_VIDEO_UPLOAD_MAX_DURATION_SECONDS;

  if (durationUnknown && !overSize) {
    return 'unknown';
  }

  if (overSize && overDuration) {
    return 'both';
  }

  if (overSize) {
    return 'size';
  }

  if (overDuration) {
    return 'duration';
  }

  return null;
};

export const getVideoUploadLimitDetail = (rejections: CanvasVideoUploadRejectionReason[]): string => {
  if (rejections.length === 0) {
    return '';
  }

  const hasSize = rejections.some((r) => r === 'size' || r === 'both');
  const hasDuration = rejections.some((r) => r === 'duration' || r === 'both');
  const hasUnknown = rejections.some((r) => r === 'unknown');

  if (hasUnknown && !hasSize && !hasDuration) {
    return 'exceeds size or duration limit';
  }

  const parts: string[] = [];

  if (hasSize) {
    parts.push(`exceeds ${getCanvasVideoUploadMaxSizeLabel()}`);
  }
  if (hasDuration) {
    parts.push(`exceeds ${CANVAS_VIDEO_UPLOAD_MAX_DURATION_SECONDS}s`);
  }

  return parts.join(' and ');
};

export const getVideoUploadRejectionMessage = (rejections: CanvasVideoUploadRejectionReason[]): string => {
  if (rejections.length === 0) {
    return '';
  }

  const detail = getVideoUploadLimitDetail(rejections);
  const count = rejections.length;

  return `${pluralize('video', count, true)} couldn't be added (${detail})`;
};
